import React from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";

const TITLE_BG = "#1F4E78";
const HEADER_BG = "#305496";
const BORDER = "#000000";
const ROW_ALT_BG = "#F5F8FC";
const FOOTER_COLOR = "#555555";
const ACCUSED_COL_COUNT = 6;
const DETAIL_ROW_MIN_HEIGHT = 22;

const styles = StyleSheet.create({
  page: {
    paddingTop: 14,
    paddingBottom: 24,
    paddingHorizontal: 10,
    fontFamily: "kokila",
    fontSize: 6,
  },
  titleBar: {
    backgroundColor: TITLE_BG,
    paddingVertical: 8,
    paddingHorizontal: 6,
    marginBottom: 5,
    width: "100%",
  },
  titleText: {
    color: "#FFFFFF",
    fontFamily: "kokila",
    fontSize: 12,
    fontWeight: "bold",
    textAlign: "center",
  },
  subTitleText: {
    color: TITLE_BG,
    fontFamily: "kokila",
    fontSize: 10,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 3,
  },
  dateRangeText: {
    color: TITLE_BG,
    fontFamily: "kokila",
    fontSize: 8,
    textAlign: "center",
    marginBottom: 8,
  },
  table: {
    width: "100%",
    borderWidth: 1,
    borderColor: BORDER,
    borderStyle: "solid",
  },
  tableHeaderRow: {
    flexDirection: "row",
    width: "100%",
    alignItems: "stretch",
  },
  accusedGroupRow: {
    flexDirection: "row",
    width: "100%",
    alignItems: "stretch",
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
    borderStyle: "solid",
  },
  accusedBlock: {
    flexDirection: "row",
    alignItems: "stretch",
    alignSelf: "stretch",
    borderRightWidth: 1,
    borderRightColor: BORDER,
    borderStyle: "solid",
  },
  detailRowsBlock: {
    flexDirection: "column",
    alignSelf: "stretch",
  },
  detailHeaderRow: {
    flexDirection: "row",
    alignItems: "stretch",
    width: "100%",
  },
  detailRow: {
    flexDirection: "row",
    alignItems: "stretch",
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
    borderStyle: "solid",
  },
  detailRowLast: {
    borderBottomWidth: 0,
  },
  footer: {
    fontFamily: "kokila",
    marginTop: 10,
    fontSize: 8,
    color: FOOTER_COLOR,
    fontStyle: "italic",
    textAlign: "center",
  },
});

const formatCell = (value, showDash = true) => {
  if (value === null || value === undefined || value === "") {
    return showDash ? "-" : "";
  }
  return String(value);
};

const getHeaderCellStyle = (widthPercent, isLast) => ({
  width: `${widthPercent}%`,
  borderRightWidth: isLast ? 0 : 1,
  borderRightColor: BORDER,
  borderBottomWidth: 1,
  borderBottomColor: BORDER,
  borderStyle: "solid",
  paddingVertical: 6,
  paddingHorizontal: 2,
  justifyContent: "center",
  backgroundColor: HEADER_BG,
  minHeight: 28,
});

const getDetailCellStyle = (widthPercent, isLast, zebraBg) => ({
  width: `${widthPercent}%`,
  borderRightWidth: isLast ? 0 : 1,
  borderRightColor: BORDER,
  borderStyle: "solid",
  paddingVertical: 4,
  paddingHorizontal: 2,
  justifyContent: "center",
  minHeight: DETAIL_ROW_MIN_HEIGHT,
  ...(zebraBg ? { backgroundColor: zebraBg } : {}),
});

const getMergedAccusedCellStyle = (widthPercent, isLast, zebraBg, minHeight) => ({
  width: `${widthPercent}%`,
  borderRightWidth: isLast ? 0 : 1,
  borderRightColor: BORDER,
  borderStyle: "solid",
  paddingVertical: 4,
  paddingHorizontal: 2,
  justifyContent: "center",
  alignSelf: "stretch",
  minHeight,
  ...(zebraBg ? { backgroundColor: zebraBg } : {}),
});

const getTextStyle = (isHeader) => ({
  fontFamily: "kokila",
  fontSize: isHeader ? 6 : 5.5,
  fontWeight: isHeader ? "bold" : "normal",
  color: isHeader ? "#FFFFFF" : "#000000",
  textAlign: "center",
  lineHeight: 1.25,
});

const HeaderCell = ({ widthPercent, isLast, children }) => (
  <View style={getHeaderCellStyle(widthPercent, isLast)}>
    <Text style={getTextStyle(true)}>{children}</Text>
  </View>
);

const DetailCell = ({ widthPercent, isLast, zebraBg, children }) => (
  <View style={getDetailCellStyle(widthPercent, isLast, zebraBg)}>
    <Text style={getTextStyle(false)}>{children}</Text>
  </View>
);

const MergedAccusedCell = ({
  widthPercent,
  isLast,
  zebraBg,
  minHeight,
  children,
}) => (
  <View
    style={getMergedAccusedCellStyle(widthPercent, isLast, zebraBg, minHeight)}
  >
    <Text style={getTextStyle(false)}>{children}</Text>
  </View>
);

/** Default width percents for 16-column गुन्ह्यासहित यादी layout */
export const GUNA_LIST_COLUMN_WIDTHS = [
  4, 7, 7, 9, 5, 4, 7, 7, 6, 5, 6, 11, 8, 7, 5, 5,
];

const toRelativeWidths = (sectionWidths) => {
  const total = sectionWidths.reduce((sum, w) => sum + w, 0) || 1;
  return sectionWidths.map((w) => (w / total) * 100);
};

const getSectionPercents = (accusedWidths, detailWidths) => {
  const totalWeight =
    accusedWidths.reduce((s, w) => s + w, 0) +
    detailWidths.reduce((s, w) => s + w, 0) ||
    1;

  return {
    accusedSectionPercent:
      (accusedWidths.reduce((s, w) => s + w, 0) / totalWeight) * 100,
    detailSectionPercent:
      (detailWidths.reduce((s, w) => s + w, 0) / totalWeight) * 100,
  };
};

const TableHeaderRow = ({
  headers,
  accusedWidths,
  detailWidths,
  accusedSectionPercent,
  detailSectionPercent,
}) => {
  const accusedHeaders = headers.slice(0, ACCUSED_COL_COUNT);
  const detailHeaders = headers.slice(ACCUSED_COL_COUNT);
  const accusedRelative = toRelativeWidths(accusedWidths);
  const detailRelative = toRelativeWidths(detailWidths);

  return (
    <View style={styles.tableHeaderRow} fixed>
      <View
        style={[styles.accusedBlock, { width: `${accusedSectionPercent}%` }]}
      >
        {accusedHeaders.map((label, index) => (
          <HeaderCell
            key={`h-accused-${index}`}
            widthPercent={accusedRelative[index]}
            isLast={index === accusedHeaders.length - 1}
          >
            {label}
          </HeaderCell>
        ))}
      </View>

      <View style={{ width: `${detailSectionPercent}%` }}>
        <View style={styles.detailHeaderRow}>
          {detailHeaders.map((label, index) => (
            <HeaderCell
              key={`h-detail-${index}`}
              widthPercent={detailRelative[index]}
              isLast={index === detailHeaders.length - 1}
            >
              {label}
            </HeaderCell>
          ))}
        </View>
      </View>
    </View>
  );
};

const AccusedGroupRow = ({
  accused,
  detailRows,
  accusedWidths,
  detailWidths,
  accusedSectionPercent,
  detailSectionPercent,
  zebraBg,
}) => {
  const accusedRelative = toRelativeWidths(accusedWidths);
  const detailRelative = toRelativeWidths(detailWidths);
  const groupMinHeight = detailRows.length * DETAIL_ROW_MIN_HEIGHT;

  return (
    <View style={styles.accusedGroupRow} wrap={false}>
      <View
        style={[
          styles.accusedBlock,
          {
            width: `${accusedSectionPercent}%`,
            minHeight: groupMinHeight,
          },
        ]}
      >
        {accused.map((cell, index) => (
          <MergedAccusedCell
            key={`accused-${index}`}
            widthPercent={accusedRelative[index]}
            isLast={index === accused.length - 1}
            zebraBg={zebraBg}
            minHeight={groupMinHeight}
          >
            {formatCell(cell)}
          </MergedAccusedCell>
        ))}
      </View>

      <View style={{ width: `${detailSectionPercent}%` }}>
        <View style={styles.detailRowsBlock}>
          {detailRows.map((detailRow, rowIndex) => (
            <View
              key={`detail-${rowIndex}`}
              style={[
                styles.detailRow,
                rowIndex === detailRows.length - 1 ? styles.detailRowLast : {},
              ]}
            >
              {detailRow.map((cell, colIndex) => (
                <DetailCell
                  key={`detail-${rowIndex}-${colIndex}`}
                  widthPercent={detailRelative[colIndex]}
                  isLast={colIndex === detailRow.length - 1}
                  zebraBg={zebraBg}
                >
                  {formatCell(cell, false)}
                </DetailCell>
              ))}
            </View>
          ))}
        </View>
      </View>
    </View>
  );
};

export const GunaListPdfDocument = ({
  headerText,
  reportTitle,
  dateRangeText,
  headers = [],
  accusedGroups = [],
  footerText,
  columnWidths = GUNA_LIST_COLUMN_WIDTHS,
}) => {
  const widths =
    headers.length === columnWidths.length
      ? columnWidths
      : headers.map(() => 100 / Math.max(headers.length, 1));

  const accusedWidths = widths.slice(0, ACCUSED_COL_COUNT);
  const detailWidths = widths.slice(ACCUSED_COL_COUNT);
  const { accusedSectionPercent, detailSectionPercent } = getSectionPercents(
    accusedWidths,
    detailWidths,
  );

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page} wrap>
        {headerText ? (
          <View style={styles.titleBar}>
            <Text style={styles.titleText}>{headerText}</Text>
          </View>
        ) : null}

        {reportTitle ? (
          <Text style={styles.subTitleText}>{reportTitle}</Text>
        ) : null}

        {dateRangeText ? (
          <Text style={styles.dateRangeText}>{dateRangeText}</Text>
        ) : null}

        <View style={styles.table}>
          <TableHeaderRow
            headers={headers}
            accusedWidths={accusedWidths}
            detailWidths={detailWidths}
            accusedSectionPercent={accusedSectionPercent}
            detailSectionPercent={detailSectionPercent}
          />

          {accusedGroups.map((group, groupIndex) => {
            const zebraBg = groupIndex % 2 === 1 ? ROW_ALT_BG : "#FFFFFF";
            return (
              <AccusedGroupRow
                key={groupIndex}
                accused={group.accused}
                detailRows={group.detailRows}
                accusedWidths={accusedWidths}
                detailWidths={detailWidths}
                accusedSectionPercent={accusedSectionPercent}
                detailSectionPercent={detailSectionPercent}
                zebraBg={zebraBg}
              />
            );
          })}
        </View>

        {footerText ? <Text style={styles.footer}>{footerText}</Text> : null}
      </Page>
    </Document>
  );
};
